import { List } from "immutable";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import * as BS from "react-bootstrap";
import { TableHeaderColumn } from "react-bootstrap-table";

import { EditNavItemModal, EditNavRedirectModal, EditNavSeparatorModal } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { Statuses, NavigationTypes } from "../../../domain";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";
import Settings from "../../../settings";

type ListSortType = "az" | "hierarchy";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    dataLoading: state.adminNavListState.loading,
    navItems: state.adminNavListState.navItems,
    types: state.adminNavListState.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    dispatchUrl: (url: string) => dispatch(push(url)),
    loadData: () => dispatch(Actions.AdminNavList.loadData()),
    notifyError: (message: string, icon?: string) => dispatch(Actions.Noti.notifyError(message, icon)),
    notifySuccess: (message: string, icon?: string) => dispatch(Actions.Noti.notifySuccess(message, icon)),
    expectNavigationToReload: () => dispatch(Actions.Nav.startExpectedRefetchTimer()),
    updateNavItem: (navItems: List<M.AdminNav.NavListItem>) => dispatch(Actions.AdminNavList.updateNavItems(navItems)),
});

interface ConnectedState {
    api: Api;
    dataLoading: boolean;
    navItems: List<M.AdminNav.NavListItem>;
    types: List<M.Generic.TypeDefinition>;
}

interface ConnectedDispatch {
    dispatchUrl: (url: string) => void;
    loadData: () => void;
    notifyError: (message: string, icon?: string) => void;
    notifySuccess: (message: string, icon?: string) => void;
    expectNavigationToReload: () => void;
    updateNavItem: (navItems: List<M.AdminNav.NavListItem>) => void;
}

interface RouteParams {
    action?: string;
    itemId?: string;
}

interface OwnProps extends RouteComponentProps<RouteParams> {
}

interface OwnState {
    doFilter: boolean;
    filterBySectionId?: number;
    initialized: boolean;
    navItemsFiltered: M.AdminNav.NavListItem[];
    navTypes: { [index: number]: M.Generic.TypeDefinition; };
    sortType: ListSortType;
    statuses: { [index: number]: M.Generic.TypeDefinition; };
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _table: Comp.Table.BootstrapTableEnhanced | null = null;
    private _editNavItemModal: EditNavItemModal | null = null;
    private _editNavRedirectModal: EditNavRedirectModal | null = null;
    private _editNavSeparatorModal: EditNavSeparatorModal | null = null;

    public constructor(props: any) {
        super(props);

        this.state = {
            doFilter: false,
            initialized: false,
            loading: false,
            navItemsFiltered: [],
            navTypes: {},
            sortType: "hierarchy",
            statuses: Utility.createNumberIndexMap(Statuses.Values, (s) => s.id),
        };
    }

    public componentWillMount() {
        this.props.loadData();
    }

    public componentDidMount() {
        this._isMounted = true;

        this.checkRouteParams(this.props.match.params);
        this.triggerDataFilter();
    }

    public componentDidUpdate() {
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public componentWillReceiveProps(nextProps: Readonly<CombinedProps>, nextContext: any) {
        const routeParams = nextProps.match.params;

        if (routeParams.action !== this.props.match.params.action
            || routeParams.itemId !== this.props.match.params.itemId) {
            // route props have changed
            this.checkRouteParams(routeParams);
        }

        this.triggerDataFilter();
    }

    public shouldComponentUpdate?(nextProps: Readonly<CombinedProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        if (nextState.doFilter) {
            this.filterData(nextProps.navItems);
            return false;
        }

        return true;
    }

    public render() {
        const navItems = this.props.navItems.toArray();
        const navTypes = Utility.createNumberIndexMap(this.props.types.toArray(), (t) => t.id);

        // create sections filter dropdown
        const mainSections = navItems.filter((n) => n.branchLevel === 1 && n.onNav);
        let sectionsMenu = null;
        if (mainSections.length) {
            let dropTitle = "All sections";
            sectionsMenu = mainSections.map((s, i) => {
                if (s.sectionId === this.state.filterBySectionId)
                    dropTitle = s.title;
                return <BS.MenuItem key={s.sectionId} onClick={() => this.updateFilterBySectionId(s.sectionId)}>{s.title}</BS.MenuItem>;
            });

            // add all sections item
            sectionsMenu.unshift(<BS.MenuItem key={0} onClick={() => this.updateFilterBySectionId()}>All sections</BS.MenuItem>);

            // compose dropdown
            sectionsMenu = <BS.DropdownButton id="" title={dropTitle}>{sectionsMenu}</BS.DropdownButton>;
        }

        // create sort dropdown
        const sortMenu = <BS.DropdownButton id="" title="Sort by">
            <BS.MenuItem onClick={() => this.updateSortType("az")}>Sort A&ndash;Z</BS.MenuItem>
            <BS.MenuItem onClick={() => this.updateSortType("hierarchy")}>Sort by hierarchy</BS.MenuItem>
        </BS.DropdownButton>;

        const actionsPrimary = <BS.ButtonToolbar>
                <BS.ButtonGroup>
                    <BS.SplitButton bsStyle="default" id="" title="Add new item" onClick={() => this.toggleEditModal()}>
                        <BS.MenuItem onClick={() => this.toggleEditModal(undefined, NavigationTypes.Redirect.id)}>Redirect</BS.MenuItem>
                        <BS.MenuItem onClick={() => this.toggleEditModal(undefined, NavigationTypes.Separator.id)}>Separator</BS.MenuItem>
                    </BS.SplitButton>
                </BS.ButtonGroup>
                <BS.ButtonGroup>
                    {sortMenu}
                    {sectionsMenu}
                </BS.ButtonGroup>
            </BS.ButtonToolbar>;

        return <Comp.Page.AdminPage breadcrumb={[ <Link to={Paths.Admin.Landing}>Admin</Link> ]} title="Navigation">
            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.deleteNavItem()}
                title="Confirm delete"
                ref={(ref) => this._confirmDelete = ref}
            >
                <p>Are you sure you want to delete this navigation item?</p>
            </Comp.Modal.ConfirmModal>

            <EditNavItemModal
                api={this.props.api}
                navItemsGetter={() => navItems}
                onCancel={() => this.removeActionFromUrl()}
                onUpdated={(navItem) => this.onNavItemSaved(navItem)}
                ref={(ref) => this._editNavItemModal = ref}
            />

            <EditNavRedirectModal
                api={this.props.api}
                navItemsGetter={() => navItems}
                onCancel={() => this.removeActionFromUrl()}
                onUpdated={(navItem) => this.onNavItemSaved(navItem)}
                ref={(ref) => this._editNavRedirectModal = ref}
            />

            <EditNavSeparatorModal
                api={this.props.api}
                navItemsGetter={() => navItems}
                onCancel={() => this.removeActionFromUrl()}
                onUpdated={(navItem) => this.onNavItemSaved(navItem)}
                ref={(ref) => this._editNavSeparatorModal = ref}
            />

            <div className="results" style={{ position: "relative" }}>
                <Comp.Generic.LoadingSpinner show={this.state.loading || this.props.dataLoading} />

                <div className="results-list">
                    <Comp.Table.BootstrapTableEnhanced
                        actionsPrimary={actionsPrimary}
                        bsTable={{
                            data: this.state.navItemsFiltered,
                            options: {
                                // onSortChange: (name: any, order: any) => { console.log("sort", name, order); },
                            },
                        }}
                        totalDataRows={navItems.length}
                        ref={(ref) => this._table = ref}
                    >
                        <TableHeaderColumn
                            dataField="id"
                            isKey
                            hidden
                            searchable={false}
                        ></TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="id"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => row.parentIds.length
                                ? <span>
                                    <Link to={Paths.AdminNav.List + "/edit/" + row.id}>Edit</Link>
                                    {row.hasChildren ? <span> | <Link to={Paths.AdminNav.SortItems + "/" + row.id}>Sort</Link></span> : null}
                                </span>
                                : ""}
                            searchable={false}
                            width="105px"
                        ></TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="position"
                            dataSort
                            hidden
                            searchable={false}
                            width="200px"
                        >Position</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="sectionId"
                            dataSort
                            hidden
                            width="50px"
                        >Sec</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="title"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => this.formatColTitle(row)}
                            dataSort
                        >Title</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="typeId"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => Comp.Table.BootstrapTableHelpers.formatByIndexMap(row, (n) => n.typeId, navTypes, (s) => s.name)}
                            dataSort
                            searchable={false}
                            width="200px"
                        >Type</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="onNav"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => Comp.Table.BootstrapTableHelpers.formatBooleanAsIcon(row, (n) => n.onNav, "check", "")}
                            dataSort
                            defaultASC={false}
                            searchable={false}
                            width="100px"
                        >Shown</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="locked"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => Comp.Table.BootstrapTableHelpers.formatBooleanAsIcon(row, (n) => n.locked, "lock", "")}
                            dataSort
                            searchable={false}
                            width="100px"
                        >Locked</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="statusId"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) => Comp.Table.BootstrapTableHelpers.formatByIndexMap(row, (n) => n.statusId, this.state.statuses, (s) => s.name)}
                            dataSort
                            searchable={false}
                            width="100px"
                        >Status</TableHeaderColumn>

                        <TableHeaderColumn
                            dataField="id"
                            dataFormat={(cell, row: M.AdminNav.NavListItem) =>
                                row.locked
                                    ? ""
                                    : <Comp.Generic.IconLink className="req" icon="times" onClick={() => this.toggleDeleteConfirmation(row)} to="#" />}
                            searchable={false}
                            width="40px"
                        ></TableHeaderColumn>

                    </Comp.Table.BootstrapTableEnhanced>
                </div>
            </div>
        </Comp.Page.AdminPage>;
    }

    /**
     * Checks given route params and performs actions based on values
     * @param routeParams The url route params
     */
    private checkRouteParams(routeParams: RouteParams) {
        if (routeParams.action && routeParams.action.toLowerCase() === "edit") {
            // open edit dialog...
            const itemId = parseInt(routeParams.itemId || "", 10);
            if (isNaN(itemId))
                this.props.notifyError("Invalid navigation item ID");
            else
                this.toggleEditModal(itemId);
        }
        else if (this._editNavItemModal && this._editNavRedirectModal && this._editNavSeparatorModal) {
            if (this._editNavItemModal.state.show)
                this._editNavItemModal.close();
            if (this._editNavRedirectModal.state.show)
                this._editNavRedirectModal.close();
            if (this._editNavSeparatorModal.state.show)
                this._editNavSeparatorModal.close();
        }
    }

    private async deleteNavItem() {
        if (this._confirmDelete === null)
            return;

        const navItem = this._confirmDelete.getRef() as M.AdminNav.NavListItem;
        if (navItem === undefined) {
            this.props.notifyError("Navigation item is undefined in the confirmation modal.");
            return;
        }

        const queryParams = { id: navItem.id };
        this.props.api.delete<boolean>(Api.endpoints.admin.nav.item, { queryParams })
            .then((result) => {
                if (result.isSuccess) {
                    this.onNavItemDeleted(navItem);
                    this.props.notifySuccess(`Navigation item <strong>${navItem.title}</strong> has been successfully deleted.`);
                }
                else {
                    let error = result.errors.join("<br />");
                    if (result.errorCode === 402) // when nav item has children
                        error = `Navigation item <strong>${navItem.title}</strong> has children.<br />Delete children first before deleting this item.`;
                    throw new Error (error);
                }
            }, (err) => {

            })
            .catch((err) => {
                this.props.notifyError(err);
            })
            .then(() => {
                if (this._confirmDelete !== null)
                    this._confirmDelete.close();
            });
    }

    private filterData(navItems: List<M.AdminNav.NavListItem>) {
        const navItemsFiltered: M.AdminNav.NavListItem[] = [];

        navItems.forEach((n, i) => {
            if (n) {
                const isFiltered = !this.state.filterBySectionId
                    || (n.sectionId === this.state.filterBySectionId);

                if (isFiltered) {
                    navItemsFiltered.push(n);
                }
            }
        });

        this.setState({
            ...this.state,
            doFilter: false,
            navItemsFiltered,
        });
    }

    private formatColTitle(navItem: M.AdminNav.NavListItem) {
        let link = null;
        let title: string | JSX.Element = navItem.title;

        if (navItem.typeId === NavigationTypes.Separator.id)
            title = <span>&ndash;Separator&ndash;</span>;

        if (navItem.statusId === 1 && navItem.url)
            link = <Link to={(navItem.url || "")} target="_blank"><i className="fa fa-external-link"></i></Link>;

        if (navItem.fullTitle.indexOf(" > ") === -1)
            return <span>{navItem.title} {link}</span>;

        return <span>{title} {link}<br /><span className="mute">{navItem.fullTitle}</span></span>;
    }

    /**
     * Callback after navigation item is deleted
     * @param navItem The newly saved navigation item
     */
    private onNavItemDeleted(navItem: M.AdminNav.NavListItem) {
        const idx = this.props.navItems.findIndex((n) => n !== undefined && n.id === navItem.id);

        if (idx > -1) {
            const navItems = this.props.navItems.delete(idx);

            // trigger a refetch of navigation items for the app
            this.props.expectNavigationToReload();
            this.props.updateNavItem(navItems);
        }
    }

    /**
     * Callback after navigation item is saved
     * @param navItem The newly saved navigation item
     */
    private onNavItemSaved(navItem: M.AdminNav.NavListItem) {
        const idx = this.props.navItems.findIndex((n) => n !== undefined && n.id === navItem.id);
        let navItems = List<M.AdminNav.NavListItem>();

        if (idx > -1) {
            navItems = this.props.navItems.update(idx, (ni) => navItem);
        }
        else {
            navItems = this.props.navItems.push(navItem);
            this.props.loadData();
        }

        // trigger a refetch of navigation items for the app
        this.props.expectNavigationToReload();
        this.props.updateNavItem(navItems);
        this.removeActionFromUrl();

        const title = navItem.title.length > 20 ? (navItem.title.substr(0, 20) + "&hellip;") : navItem.title;
        this.props.notifySuccess(`Navigation item <strong>${title}</strong> has been successfully saved.`);
    }

    private removeActionFromUrl() {
        this.props.dispatchUrl(Paths.AdminNav.List);
    }

    private toggleDeleteConfirmation(navItem: M.AdminNav.NavListItem) {
        if (this._confirmDelete !== null) {
            this._confirmDelete.show(navItem);
        }
    }

    private toggleEditModal(itemId?: number, typeId?: number) {
        if (!this._editNavItemModal || !this._editNavRedirectModal || !this._editNavSeparatorModal)
            return;

        if (itemId) {
            // getting nav item for edit:
            this.toggleLoading(true);

            this.props.api.get<M.AdminNav.NavListItem>(Api.endpoints.admin.nav.item, { queryParams: { id: itemId, appId: Settings.AppID } })
                .then((result) => {
                    if (this._isMounted && this._editNavItemModal && this._editNavRedirectModal && this._editNavSeparatorModal) {
                        if (result.isSuccess && result.data) {
                            // success, show dialog
                            if (!result.data.parentIds.length)
                                throw new Error("Editting the home page is not allowed.");
                            else if (NavigationTypes.isContentType(result.data.typeId))
                                this._editNavItemModal.show(result.data);
                            else if (result.data.typeId === NavigationTypes.Redirect.id)
                                this._editNavRedirectModal.show(result.data);
                            else if (result.data.typeId === NavigationTypes.Separator.id)
                                this._editNavSeparatorModal.show(result.data);
                        }
                        else
                            throw new Error(result.errors.join("<br />"));
                    }
                }, (err) => {
                    // error getting item
                    throw new Error(err);
                })
                .catch((err) => {
                    this.props.notifyError("Unable to load navigation item record:<br />" + err);
                    this.removeActionFromUrl();
                })
                .then(() => {
                    this.toggleLoading(false);
                });
        }
        else {
            // adding new nav item:
            if (!typeId || NavigationTypes.isContentType(typeId))
                this._editNavItemModal.show();
            else if (typeId === NavigationTypes.Redirect.id)
                this._editNavRedirectModal.show();
            else if (typeId === NavigationTypes.Separator.id)
                this._editNavSeparatorModal.show();
        }
    }

    private triggerDataFilter() {
        this.setState({ ...this.state, doFilter: true });
    }

    private updateFilterBySectionId(filterBySectionId?: number) {
        this.setState({ ...this.state, filterBySectionId }, () => this.triggerDataFilter());
    }

    private updateSortType(sortType: ListSortType) {
        if (this._table && this._table.table) {
            this._table.table.handleSort("asc", sortType === "az" ? "title" : "position");
        }
    }
}

export const ListView: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(ListComponent);



// WEBPACK FOOTER //
// ./src/containers/adminNav/list/List.tsx